import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';
import { toast } from '../hooks/use-toast';

const Deposit = () => {
  const navigate = useNavigate();
  const { lockers, whiteLabel, generatePin, depositParcel, currentUser, recipients, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key) => getTranslation(key, language);
  
  const [step, setStep] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [generatedPin, setGeneratedPin] = useState(null);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState('');

  const availableLockers = lockers.filter(l => l.state === 'Free' && l.type !== 'Keypad');
  const totalLockers = lockers.filter(l => l.type !== 'Keypad').length;
  
  const sizes = [
    { size: 'Small', name: t('small'), desc: t('documentsSmallItems'), width: 'w-20', height: 'h-20' },
    { size: 'Medium', name: t('medium'), desc: t('boxesPackages'), width: 'w-28', height: 'h-28' },
    { size: 'Large', name: t('large'), desc: t('largeParcels'), width: 'w-36', height: 'h-36' },
  ];

  const handleSizeSelect = (size) => {
    const available = availableLockers.filter(l => l.type === size);
    if (available.length === 0) {
      toast({
        title: t('noLockers'),
        description: getTranslation('sorryAllLockers', language, { size }),
        variant: 'destructive'
      });
      return;
    }
    setSelectedSize(size);
    setStep(2);
  };

  const handleGeneratePin = () => {
    if (!selectedRecipient) {
      toast({
        title: t('selectRecipient'),
        description: t('pleaseSelectRecipient'),
        variant: 'destructive'
      });
      return;
    }
    
    const pin = generatePin();
    setGeneratedPin(pin);
    const available = availableLockers.filter(l => l.type === selectedSize);
    setSelectedLocker(available[0]);
    setStep(3);
  };

  const handleDrop = () => {
    if (selectedLocker && generatedPin) {
      depositParcel(selectedLocker.id, selectedLocker.sizeCode, generatedPin, currentUser.name, selectedRecipient);
      toast({
        title: t('depositSuccessful'),
        description: `Locker #${selectedLocker.id}. PIN: ${generatedPin}`,
      });
      setStep(4);
    }
  };

  const handleReset = () => {
    setStep(1);
    setSelectedSize(null);
    setGeneratedPin(null);
    setSelectedLocker(null);
    setSelectedRecipient('');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('sendParcel')}</h1>
        <p className="text-gray-600">{t('followSteps')}</p>
        <p className="text-sm text-gray-500">{availableLockers.length} / {totalLockers} {t('availableLockers')}</p>
      </div>

      <div className="flex items-center justify-center space-x-4 mb-8">
        {[1, 2, 3, 4].map(num => (
          <div key={num} className="flex items-center">
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white transition-all ${
                step >= num ? 'scale-110' : 'scale-100 opacity-50'
              }`}
              style={{ backgroundColor: step >= num ? primaryColor : '#9CA3AF' }}
            >
              {num}
            </div>
            {num < 4 && <div className="w-12 h-1 bg-gray-300" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('chooseLockerSize')}</CardTitle>
              <CardDescription>{t('selectSize')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {sizes.map(item => {
                  const available = availableLockers.filter(l => l.type === item.size).length;
                  return (
                    <button
                      key={item.size}
                      onClick={() => handleSizeSelect(item.size)}
                      className="p-6 border-2 rounded-xl transition-all hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2"
                      style={{ borderColor: primaryColor }}
                      disabled={available === 0}
                    >
                      <div className="flex flex-col items-center space-y-3">
                        <div className={`${item.width} ${item.height} rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${primaryColor}20` }}>
                          <Package size={item.size === 'Large' ? 48 : item.size === 'Medium' ? 36 : 24} style={{ color: primaryColor }} />
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-xl">{item.name}</div>
                          <div className="text-sm text-gray-600">{item.desc}</div>
                          <div className="text-xs mt-2 font-semibold" style={{ color: primaryColor }}>
                            {available} {t('available')}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('generateYourPin')}</CardTitle>
            <CardDescription>{t('needPinRetrieve')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('yourName')} ({t('currentUser')})
                </label>
                <input
                  type="text"
                  value={currentUser.name}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('toWhom')} *
                </label>
                <select
                  value={selectedRecipient}
                  onChange={(e) => setSelectedRecipient(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': primaryColor }}
                >
                  <option value="">-- {t('selectRecipient')} --</option>
                  {recipients.map(recipient => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <Button 
              onClick={handleGeneratePin}
              className="w-full text-lg py-6"
              style={{ backgroundColor: primaryColor }}
              disabled={!selectedRecipient}
            >
              <RefreshCw className="mr-2" size={20} />
              {t('generatePin')}
            </Button>
            <Button 
              variant="outline"
              onClick={handleReset}
              className="w-full"
            >
              {t('backToSizeSelection')}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('yourPinCode')}</CardTitle>
            <CardDescription>{t('savePinRetrieve')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-8 rounded-xl text-center" style={{ backgroundColor: `${primaryColor}10` }}>
              <p className="text-sm text-gray-600 mb-2">{t('yourPin')}</p>
              <div className="text-6xl font-bold tracking-wider" style={{ color: primaryColor }}>
                {generatedPin}
              </div>
              <p className="text-sm text-gray-600 mt-4">{t('lockerNumber')}: {selectedLocker?.id} ({selectedLocker?.sizeCode})</p>
              <p className="text-sm text-gray-600 mt-2">{t('toWhom')}: {selectedRecipient}</p>
            </div>
            <Button 
              onClick={handleDrop}
              className="w-full text-lg py-6"
              style={{ backgroundColor: primaryColor }}
            >
              {t('simulateDrop')}
            </Button>
            <Button 
              variant="outline"
              onClick={handleReset}
              className="w-full"
            >
              {t('cancel')}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle size={64} className="text-green-600" />
            </div>
            <CardTitle className="text-center text-2xl">{t('depositSuccessful')}!</CardTitle>
            <CardDescription className="text-center">{t('parcelSecured')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-6 rounded-xl bg-green-50 border-2 border-green-200">
              <div className="space-y-2 text-center">
                <p className="text-sm text-gray-600">{t('lockerNumber')}</p>
                <p className="text-3xl font-bold text-[#1A1A1A]">#{selectedLocker?.id}</p>
                <p className="text-sm text-gray-600 mt-4">{t('yourPin')}</p>
                <p className="text-4xl font-bold" style={{ color: primaryColor }}>{generatedPin}</p>
                <p className="text-sm text-gray-600 mt-4">{t('toWhom')}: {selectedRecipient}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/lockers')}
                className="w-full"
                style={{ backgroundColor: primaryColor }}
              >
                {t('viewMap')}
              </Button>
              <Button 
                variant="outline"
                onClick={handleReset}
                className="w-full"
              >
                {t('depositAnother')}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                {t('backToHome')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Deposit;
